import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Swal from 'sweetalert2'

import { CONCEPT_NAME, TApi } from '../../../../shared/const'
import http from '../../../../shared/http'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'

import Quiz from './Quiz'
import CourseParticipation from './CourseParticipation'
import TeamProgress from './TeamProgress'
import ConceptProgress from '../../../../UI/ConceptProgress'
import ConceptField from './ConceptField'
import HandbookButton from '../../common/Concept/HandbookButton'

import { CHAPTERS } from './chapters'

import styles from './style.module.scss'

/**
 * Marketing & E-Commerce: die Lernstrecke und das Konzept, das die
 * Junior Company dazu schreibt.
 *
 * Zehn Kapitel, jedes mit einem Quiz und danach den Konzeptfeldern, die aus
 * diesem Kapitel folgen. Der letzte Reiter zeigt alle fünfzehn Felder
 * zusammen; dort wird das Konzept auch abgeschlossen.
 *
 * Zwei Dinge sind hier bewusst nicht in der Oberfläche gerechnet: ob ein
 * Kapitel bestanden ist und ob abgeschlossen werden darf. Beides sagt der
 * Server — jede Antwort bringt den vollständigen neuen Stand mit, und die
 * Seite zeigt ihn nur an.
 *
 * Das Quiz gehört der einzelnen Person, das Konzept der Junior Company:
 * `students` enthält alle Mitarbeiter:innen, gezeigt wird hier der eigene
 * Lernstand.
 */

const ALLE = 'alle'

const STATUS_TEXT = {
	DRAFT: 'In Arbeit',
	RETURNED: 'Von der Lehrkraft zur Überarbeitung zurückgegeben',
	SUBMITTED: 'Zur Bewertung abgeschlossen',
	SCORED: 'Von der Lehrkraft bewertet'
}

export default function Concept() {
	const eigeneId = useSelector(state => state.user.profile.id)

	const [daten, setDaten] = useState(null)
	const [laedt, setLaedt] = useState(true)
	const [ansicht, setAnsicht] = useState(1)
	const [sendet, setSendet] = useState(false)

	useEffect(() => {
		http.get(TApi.STUDENT_CONCEPT)
			.then(({ data }) => setDaten(data))
			.catch(() => {}) // die Meldung kommt aus shared/http
			.finally(() => setLaedt(false))
	}, [])

	// Beim Wechsel des Kapitels nach oben — sonst steht man mitten im neuen Text.
	useEffect(() => window.scrollTo(0, 0), [ansicht])

	const speichereFeld = useCallback(
		(fieldNo, text) =>
			http
				.put(TApi.STUDENT_CONCEPT_FIELD(fieldNo), { text }, { disableToast: true })
				.then(({ data }) => setDaten(data)),
		[]
	)

	const setzeZuordnung = useCallback(
		(fieldNo, userIds) =>
			http
				.put(TApi.STUDENT_CONCEPT_ASSIGNMENTS(fieldNo), { userIds })
				.then(({ data }) => setDaten(data))
				.catch(() => {}),
		[]
	)

	const meldeQuiz = useCallback(
		payload =>
			http
				.post(TApi.STUDENT_CONCEPT_QUIZ, payload)
				.then(({ data }) => setDaten(data))
				.catch(() => {}),
		[]
	)

	const abschliessen = async () => {
		const { isConfirmed } = await Swal.fire({
			title: 'Konzept zur Bewertung abschließen?',
			html:
				'Danach könnt ihr nichts mehr ändern. Eure betreuende Lehrkraft bewertet die fünfzehn ' +
				'Felder mit je 0 bis 10 Punkten.<br><br>Sie kann euch das Konzept auch wieder zur ' +
				'Überarbeitung freigeben.',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#008B9C',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Abschließen',
			cancelButtonText: 'Abbrechen'
		})

		if (!isConfirmed) return

		setSendet(true)
		http.post(TApi.STUDENT_CONCEPT_SUBMIT)
			.then(({ data }) => {
				setDaten(data)
				toast.success('Das Konzept wurde zur Bewertung abgeschlossen.')
			})
			.catch(() => {})
			.finally(() => setSendet(false))
	}

	const felderNach = useMemo(() => {
		const map = new Map()
		;(daten?.fields || []).forEach(field => map.set(field.no, field))

		return map
	}, [daten])

	// Der eigene Lernstand. `students` führt alle Mitarbeiter:innen — wessen
	// Quiz hier zählt, entscheidet die angemeldete Person, nicht die Reihenfolge.
	const eigenerStand = useMemo(() => {
		const ich = (daten?.students || []).find(student => student.id === eigeneId)

		return new Map((ich?.chapters || []).map(eintrag => [eintrag.chapter, eintrag]))
	}, [daten, eigeneId])

	if (laedt) return <Loading />
	// Auch ohne Konzept (etwa noch ohne Junior Company) bleibt der Kopf mit dem
	// Handbuch stehen: Nachlesen geht vor dem Mitmachen.
	if (!daten)
		return (
			<div className={styles.root}>
				<MiniHeader title={`Skills4Retail: ${CONCEPT_NAME}`}>
					<HandbookButton />
				</MiniHeader>
			</div>
		)

	const { state, students, config } = daten
	const gesperrt = daten.status === 'SUBMITTED' || daten.status === 'SCORED'
	const kapitel = CHAPTERS.find(eintrag => eintrag.no === ansicht)
	// Nach dem letzten Kapitel geht es nicht ins nächste, sondern an die Arbeit:
	// zu den fünfzehn Feldern.
	const naechstesKapitel = CHAPTERS.find(eintrag => eintrag.no === ansicht + 1)
	const punkte = (daten.fields || []).filter(field => field.score !== null && field.score !== undefined)

	return (
		<div className={styles.root}>
			<MiniHeader title={`Skills4Retail: ${CONCEPT_NAME}`}>
				<HandbookButton />
			</MiniHeader>

			<div className={clsx(styles.status, state?.passed && styles.statusDone)}>
				<div className={styles.statusText}>
					<b>{STATUS_TEXT[daten.status] || daten.status}</b>
					{daten.status === 'RETURNED' && daten.returnReason && <> — „{daten.returnReason}“</>}
					{daten.status === 'SCORED' && punkte.length > 0 && (
						<>
							{' '}
							— {punkte.reduce((summe, field) => summe + field.score, 0)} von {punkte.length * 10} Punkten
						</>
					)}

					{state?.applies === false && (
						<p className={styles.statusHint}>
							Für eure Junior Company wird das Konzept zurzeit nicht verlangt. Ihr könnt es trotzdem
							schreiben — es bleibt gespeichert.
						</p>
					)}

					{/*
					 * Der Fertigstellungsgrad, gerechnet im Server. Er steht hier
					 * und nicht bei der Lernstrecke: Die Lernstrecke ist einer von
					 * vier Abschnitten, der Balken zeigt alle vier.
					 */}
					<ConceptProgress progress={daten.progress} className={styles.statusProgress} />

					{state?.open?.length > 0 && (
						<ul className={styles.statusOpen}>
							{state.open.map((satz, i) => (
								<li key={i}>{satz}</li>
							))}
						</ul>
					)}
				</div>

				{state?.canSubmit && (
					<span className={styles.statusActions}>
						<Btn onClick={abschliessen} disabled={sendet}>
							{sendet ? 'Wird abgeschlossen …' : 'Zur Bewertung abschließen'}
						</Btn>
					</span>
				)}
			</div>

			<CourseParticipation />

			<div className={styles.tabs} role="tablist">
				{CHAPTERS.map(eintrag => {
					const stand = eigenerStand.get(eintrag.no)

					return (
						<button
							key={eintrag.no}
							type="button"
							role="tab"
							aria-selected={ansicht === eintrag.no}
							title={eintrag.title}
							className={clsx(
								styles.tab,
								ansicht === eintrag.no && styles.tabActive,
								stand?.passed && styles.tabDone
							)}
							onClick={() => setAnsicht(eintrag.no)}
						>
							{eintrag.no}
						</button>
					)
				})}
				<button
					type="button"
					role="tab"
					aria-selected={ansicht === ALLE}
					className={clsx(styles.tab, styles.tabWide, ansicht === ALLE && styles.tabActive)}
					onClick={() => setAnsicht(ALLE)}
				>
					Euer Konzept
				</button>
			</div>

			{ansicht === ALLE ? (
				<div className={styles.chapterBody}>
					<h2>Euer Konzept</h2>
					<p>
						Alle fünfzehn Felder an einer Stelle. Geschrieben wird hier genauso wie im jeweiligen Kapitel —
						es ist derselbe Text.
					</p>

					<TeamProgress students={students} config={config} eigeneId={eigeneId} state={state} />

					{(daten.fields || []).map(field => (
						<ConceptField
							key={field.no}
							field={field}
							students={students}
							readOnly={gesperrt}
							onSave={speichereFeld}
							onAssign={setzeZuordnung}
						/>
					))}
				</div>
			) : (
				<>
					{/* Der Kapitelinhalt ist fest und steht in chapters.js. */}
					<div className={styles.chapterBody} dangerouslySetInnerHTML={{ __html: kapitel.html }} />

					<div className={styles.chapterBody}>
						<Quiz
							chapter={kapitel.no}
							quizPass={config.quizPass}
							stand={eigenerStand.get(kapitel.no)}
							onSubmit={meldeQuiz}
							onWeiter={naechstesKapitel ? () => setAnsicht(naechstesKapitel.no) : () => setAnsicht(ALLE)}
							weiterLabel={
								naechstesKapitel ? `Weiter zu Kapitel ${naechstesKapitel.no}` : 'Weiter zum Konzept'
							}
						/>
					</div>

					{kapitel.fields.length > 0 && (
						<div className={styles.chapterBody}>
							<h3>Das kommt ins Konzept</h3>
							{kapitel.fields.map(no => {
								const field = felderNach.get(no)

								return field ? (
									<ConceptField
										key={no}
										field={field}
										students={students}
										readOnly={gesperrt}
										onSave={speichereFeld}
										onAssign={setzeZuordnung}
									/>
								) : null
							})}
						</div>
					)}
				</>
			)}
		</div>
	)
}
